'use client';

import { PrimaryButton } from '@egalite/ui/Buttons/GeneralButtons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import SelectField from '@egalite/ui/Form/SelectField';
import FormSuccessMessage from '@egalite/ui/IncluiPCD/FormSuccessMessage';
import Typography from '@egalite/ui/Typography';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useState } from 'react';
import useForm from '../../lib/hooks/useForm';
import { formatCnpj, maskCnpj } from '../../lib/validation/companyRegister';
import validationSchema, { initialValues } from '../../lib/validation/contactForm';

const mutation = async values =>
	fetch('/api/v1/contactForm', {
		method: 'POST',
		body: JSON.stringify(values),
		//@ts-ignore-next-line
		duplex: 'half',
		mode: 'cors',
		cache: 'no-cache',
		credentials: 'same-origin',
		redirect: 'follow',
		headers: {
			'Content-Type': 'application/json',
		},
	})
		.then(res => res.json())
		.then(data => data);

const Form = () => {
	const [successRes, setSuccessRes] = useState(false);
	const router = useRouter();
	const searchParams = useSearchParams();

	const [formController, getFieldProps, { loading, submitting, success, errors, data, setErrors }] = useForm({
		initialValues: { ...initialValues, subject: searchParams.get('subject') ?? '' },
		validationSchema,
		mutation,
		onSuccess: useCallback(() => {
			setSuccessRes(true);
			setTimeout(() => {
				router.push('/');
			}, 3000);
		}, [router]),
	});

	if (successRes) {
		return (
			<FormSuccessMessage scrollIntoView={true}>
				<>
					<Typography variant="headline-3">Contato enviado com sucesso!</Typography>
					<Typography variant="body-1">Você será encaminhado para o início...</Typography>
				</>
			</FormSuccessMessage>
		);
	}

	return (
		<form onSubmit={formController.handleSubmit}>
			<fieldset disabled={loading as boolean}>
				<SelectField
					id="subject"
					label={'Assunto'}
					{...getFieldProps('subject')}
					options={[
						{ value: 'Ajuda para CANDIDATOS', label: 'Ajuda para CANDIDATOS' },
						{ value: 'Suporte para EMPRESAS', label: 'Suporte para EMPRESAS' },
						{
							value: 'Outros contatos (imprensa, parcerias, etc.)',
							label: 'Outros contatos (imprensa, parcerias, etc.)',
						},
					]}
				/>
				{formController.values.subject === 'Suporte para EMPRESAS' && (
					<>
						<InputField id="company" type="text" label="Empresa" {...getFieldProps('company')} />
						<InputField
							id="cnpj"
							type="text"
							label="CNPJ"
							placeholder="00.000.000/0000-00"
							inputProps={{ style: { textTransform: 'uppercase' } }}
							{...getFieldProps('cnpj')}
							value={maskCnpj(formController.values.cnpj ?? '')}
							onChange={e => formController.setFieldValue('cnpj', formatCnpj(e.target.value))}
						/>
					</>
				)}
				<InputField id="name" type="text" label={'Nome completo do contato'} {...getFieldProps('name')} />
				<InputField id="email" type="email" label={'Email'} {...getFieldProps('email')} />
				<InputField
					id="phone"
					type="text"
					label={'Telefone'}
					maskOptions={{
						mask: [
							{ mask: '(00) 0000-0000', lazy: false, placeholderChar: '_' },
							{ mask: '(00) 00000-0000', lazy: false, placeholderChar: '_' },
						],
					}}
					{...getFieldProps('phone')}
				/>

				<InputField id="message" type="text" label={'Mensagem'} multiline={true} {...getFieldProps('message')} />

				<ErrorList errors={formController.errors} touched={formController.touched} />

				{errors != null &&
					(Array.isArray(errors) ? errors : [errors]).map((err, index) => (
						<Typography key={`err-${index}`} variant="body-2" className="text-error-dark">
							{err}
						</Typography>
					))}

				<PrimaryButton type="submit" variant="medium" disabled={submitting} xStyle={theme => ({ marginTop: 20 })}>
					Enviar
				</PrimaryButton>
			</fieldset>
		</form>
	);
};

const Suspended = () => {
	return <Suspense fallback={<div>Carregando...</div>}><Form /></Suspense>;
}

export default Suspended;
